import hpvScreeningIcon from "assets/images/hpv-screening.svg";
import stiScreeningIcon from "assets/images/sti-screening.svg";
import ellipseGreenIcon from "assets/images/ellipse-green.svg";
import useAnalytics from "hooks/analytics";
import useAuthUser from "hooks/auth-user";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import useLanguage from "hooks/language";
import { AlreadyBookedScreeningModal } from "modals";
import Appointment from "models/appointment";
import { ScreeningFlow, SCREENING_FLOW } from "models/screening-flow";
import User, { upcomingScreeningAppointmentForFlow } from "models/user";
import { MouseEvent, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { trackBookingFlowInitiated } from "utils/analytics/booking-flow-tracking";
import { UserActionEvent } from "utils/analytics/events";
import ROUTES from "utils/routes";
import { setScreeningFlow } from "utils/session-storage";
import styles from "./home.module.css";

type Props = {
    onScreeningLinkClick: (e: MouseEvent) => void;
};

export default function Nav({
    onScreeningLinkClick,
}: Props): React.ReactElement {
    const { translations } = useLanguage();
    const [analytics] = useAnalytics();
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const [authUser] = useAuthUser();
    const { push } = useHistory();
    const [bookedModalFlow, setBookedModalFlow] = useState<
        ScreeningFlow | undefined
    >();

    const isStiScreeningDisabled = !authUser?.isLastStiAppointmentDone;
    const isHpvScreeningDisabled = !authUser?.isLastHpvAppointmentDone;
    const bookedPopup = translations.homePage.bookedScreeningPopup;

    const bookedModalAppointment = getBookedModalAppointment(
        authUser,
        bookedModalFlow
    );

    const onCreateAppointmentClicked = (screeningFlow: ScreeningFlow) => {
        setScreeningFlow(screeningFlow);
        if (isBookingFlowRedesign) {
            trackBookingFlowInitiated(analytics, screeningFlow);
            return;
        }
        analytics
            ?.cdp()
            ?.trackEvent(UserActionEvent.ScreeningRequestedInitiated, {
                screeningType: screeningFlow,
            });
    };

    const handleBookedClick = (
        event: MouseEvent,
        screeningFlow: ScreeningFlow
    ) => {
        event.preventDefault();
        setBookedModalFlow(screeningFlow);
    };

    const handleViewAppointment = () => {
        const appointmentId = bookedModalAppointment?.id;

        setBookedModalFlow(undefined);
        push(ROUTES.APPOINTMENTS, {
            scrollToAppointmentId: appointmentId,
            scrollToAppointmentKind: "screening",
        });
    };

    const getScreeningLabel = (screeningFlow: ScreeningFlow): string => {
        if (screeningFlow === SCREENING_FLOW.HPV) {
            return translations.homePage.subheaderTab.hpv.title;
        }

        return translations.homePage.subheaderTab.sti.title;
    };

    return (
        <>
            <div className={styles["nav"]}>
                <Link
                    to="#"
                    className={`${styles["nav-link"]} ${
                        isStiScreeningDisabled ? styles["disabled-link"] : ""
                    }`}
                    onClick={(e: MouseEvent) => {
                        if (isStiScreeningDisabled) {
                            handleBookedClick(e, SCREENING_FLOW.STI);
                            return;
                        }

                        onCreateAppointmentClicked(SCREENING_FLOW.STI);
                        onScreeningLinkClick(e);
                    }}
                >
                    <div
                        className={styles["nav-overline-spacer"]}
                        aria-hidden="true"
                    />
                    <div className={styles["nav-icon"]}>
                        <img alt="" src={stiScreeningIcon} />
                    </div>
                    <div className={styles["nav-title"]}>
                        {translations.homePage.subheaderTab.sti.title}
                    </div>
                    <div className={styles["nav-subtitle"]}>
                        {translations.homePage.subheaderTab.sti.subtitle}
                    </div>
                    {isStiScreeningDisabled && (
                        <div className={styles["nav-status"]}>
                            <span className={styles["booked-chip"]}>
                                <img
                                    src={ellipseGreenIcon}
                                    alt=""
                                    className={styles["booked-chip__dot"]}
                                />
                                {bookedPopup.reservedStatus}
                            </span>
                        </div>
                    )}
                </Link>

                <Link
                    to="#"
                    className={`${styles["nav-link"]} ${
                        isHpvScreeningDisabled ? styles["disabled-link"] : ""
                    }`}
                    onClick={(e: MouseEvent) => {
                        if (isHpvScreeningDisabled) {
                            handleBookedClick(e, SCREENING_FLOW.HPV);
                            return;
                        }

                        onCreateAppointmentClicked(SCREENING_FLOW.HPV);
                        onScreeningLinkClick(e);
                    }}
                >
                    <div className={styles["nav-overline"]}>
                        {translations.homePage.subheaderTab.hpv.badge}
                    </div>
                    <div className={styles["nav-icon"]}>
                        <img alt="" src={hpvScreeningIcon} />
                    </div>
                    <div className={styles["nav-title"]}>
                        {translations.homePage.subheaderTab.hpv.title}
                    </div>
                    <div className={styles["nav-subtitle"]}>
                        {translations.homePage.subheaderTab.hpv.subtitle}
                    </div>
                    {isHpvScreeningDisabled && (
                        <div className={styles["nav-status"]}>
                            <span className={styles["booked-chip"]}>
                                <img
                                    src={ellipseGreenIcon}
                                    alt=""
                                    className={styles["booked-chip__dot"]}
                                />
                                {bookedPopup.reservedStatus}
                            </span>
                        </div>
                    )}
                </Link>
            </div>

            <AlreadyBookedScreeningModal
                appointment={bookedModalAppointment}
                screeningLabel={getScreeningLabel(
                    bookedModalFlow ?? SCREENING_FLOW.STI
                )}
                visible={bookedModalFlow !== undefined}
                onClose={() => setBookedModalFlow(undefined)}
                onViewAppointment={handleViewAppointment}
            />
        </>
    );
}

function getBookedModalAppointment(
    authUser: User | undefined,
    screeningFlow: ScreeningFlow | undefined
): Appointment | undefined {
    if (!authUser || !screeningFlow) {
        return undefined;
    }

    return upcomingScreeningAppointmentForFlow(authUser, screeningFlow);
}
